import { CloseIcon } from '@chakra-ui/icons'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Text as ChakraText,
  VStack,
} from '@chakra-ui/react'
import { cloneDeep } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, IKReactSelect } from '../../../../ui-kit'
import {
  MaxPostTransformation,
  PostTransformationLabels,
  PostTransformationTypes,
  Protocol,
  ProtocolValue,
} from '../../../constants'
import { fileConstraints, validatePostTransformation } from './uploadUtils'

const convertLabelToValue = label => {
  if (label === PostTransformationLabels.Transformation) {
    return PostTransformationTypes.Transformation
  } else if (label === PostTransformationLabels.Thumbnail) {
    return PostTransformationTypes.Thumbnail
  } else if (label === PostTransformationLabels.GifToVideo) {
    return PostTransformationTypes.GifToVideo
  } else if (label === PostTransformationLabels.ABS) {
    return PostTransformationTypes.ABS
  } else if (label === Protocol.HLS) {
    return ProtocolValue.HLS
  } else if (label === Protocol.DASH) {
    return ProtocolValue.DASH
  }
}

const convertValueToLabel = label => {
  if (label === PostTransformationTypes.Transformation) {
    return PostTransformationLabels.Transformation
  } else if (label === PostTransformationTypes.Thumbnail) {
    return PostTransformationLabels.Thumbnail
  } else if (label === PostTransformationTypes.GifToVideo) {
    return PostTransformationLabels.GifToVideo
  } else if (label === PostTransformationTypes.ABS) {
    return PostTransformationLabels.ABS
  } else if (label === ProtocolValue.HLS) {
    return Protocol.HLS
  } else if (label === ProtocolValue.DASH) {
    return Protocol.DASH
  }
}
const PostTransformationLabelsSelectMenu = ({
  fieldsMetadata,
  index,
  setFieldsMetadata,
  isDefault,
  isCommonSettings,
  type,
}): React.ReactElement => {
  const metadata = useMemo(() => fieldsMetadata[index], [fieldsMetadata, index])

  const options = useMemo(() => {
    let opt = [
      {
        label: PostTransformationLabels.Transformation,
        value: PostTransformationLabels.Transformation,
      },
    ]
    if (isCommonSettings || isDefault) {
      opt = [
        ...opt,
        {
          label: PostTransformationLabels.GifToVideo,
          value: PostTransformationLabels.GifToVideo,
        },
        {
          label: PostTransformationLabels.Thumbnail,
          value: PostTransformationLabels.Thumbnail,
        },
        {
          label: PostTransformationLabels.ABS,
          value: PostTransformationLabels.ABS,
        },
      ]
    } else {
      if (type === 'image/gif') {
        opt = [
          ...opt,
          {
            label: PostTransformationLabels.GifToVideo,
            value: PostTransformationLabels.GifToVideo,
          },
        ]
      }
      if (fileConstraints.allowedFiles.video.includes(type)) {
        opt = [
          ...opt,
          {
            label: PostTransformationLabels.Thumbnail,
            value: PostTransformationLabels.Thumbnail,
          },
          {
            label: PostTransformationLabels.ABS,
            value: PostTransformationLabels.ABS,
          },
        ]
      }
    }
    return opt
  }, [type, isCommonSettings])

  return (
    <IKReactSelect
      menuPlacement="auto"
      variant="compact"
      captureMenuScroll={false}
      value={
        metadata.optionType
          ? {
              label: metadata.optionType,
              value: metadata.optionType,
            }
          : null
      }
      placeholder="Choose type"
      onChange={newValue => {
        const newFieldsMetadata = [...fieldsMetadata]
        newFieldsMetadata[index].optionType = newValue.value
        setFieldsMetadata(newFieldsMetadata)
      }}
      options={options}
    />
  )
}

const PostTransformationTypesField = ({
  fieldsMetadata,
  index,
  setFieldsMetadata,
  isDefault,
  isCommonSettings,
  type,
}): React.ReactElement => {
  const handleValueChange = useCallback(
    newValue => {
      const newFieldsMetadata = [...fieldsMetadata]
      newFieldsMetadata[index].value = newValue
      setFieldsMetadata(newFieldsMetadata)
    },
    [fieldsMetadata, index, setFieldsMetadata],
  )

  const metadata = useMemo(() => fieldsMetadata[index], [fieldsMetadata, index])

  const valueField = useMemo(() => {
    if (metadata.optionType === PostTransformationLabels.ABS) {
      return (
        <HStack>
          <Box flex="2 0">
            <IKReactSelect
              menuPlacement="auto"
              variant="compact"
              captureMenuScroll={false}
              value={
                metadata.protocol
                  ? {
                      label: metadata.protocol,
                      value: metadata.protocol,
                    }
                  : null
              }
              placeholder="Protocol"
              onChange={newValue => {
                const newFieldsMetadata = [...fieldsMetadata]
                newFieldsMetadata[index].protocol = newValue.value
                setFieldsMetadata(newFieldsMetadata)
              }}
              options={[
                {
                  label: Protocol.HLS,
                  value: Protocol.HLS,
                },
                {
                  label: Protocol.DASH,
                  value: Protocol.DASH,
                },
              ]}
            />
          </Box>
          <Box flex="4 0">
            <Input
              value={metadata.value}
              onChange={e => handleValueChange(e.target.value)}
              placeholder="sr-240_360_480_720_1080"
            />
          </Box>
        </HStack>
      )
    } else
      return (
        <Input
          value={metadata.value}
          onChange={e => handleValueChange(e.target.value)}
          placeholder="w-40,h-30"
        />
      )
  }, [fieldsMetadata, index, metadata, handleValueChange])

  return (
    <HStack width="100%">
      <Box flex="2 0">
        <PostTransformationLabelsSelectMenu
          {...{
            fieldsMetadata,
            index,
            setFieldsMetadata,
            isDefault,
            isCommonSettings,
            type,
          }}
        />
      </Box>

      <Box flex="4 0">
        <FormControl isInvalid={metadata.error !== ''}>
          {valueField}
        </FormControl>
      </Box>
    </HStack>
  )
}

const PostTransformationFields = ({
  form,
  postTransformation,
  isDefault,
  isCommonSettings,
  type,
}): React.ReactElement => {
  const { t } = useTranslation()

  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [fieldsMetadata, setFieldsMetadata] = useState<
    Array<{
      optionType: PostTransformationLabels | null
      value: string | boolean | number
      error: string
      protocol?: string
    }>
  >([])
  const [errorMetadata, setErrorMetadata] = useState<string[]>([])

  useEffect(() => {
    if (postTransformation?.length && isInitialLoad) {
      setFieldsMetadata(
        postTransformation.map(data => {
          const ob = {
            optionType: convertValueToLabel(data.type),
            error: '',
          }
          if (data.protocol) ob['protocol'] = convertValueToLabel(data.protocol)
          if (data.value) ob['value'] = data.value
          else ob['value'] = null
          return ob
        }),
      )
    }
  }, [postTransformation, isInitialLoad])

  useEffect(() => {
    let errorData: string[] = []
    if (postTransformation?.length) {
      for (let data of postTransformation) {
        if (validatePostTransformation(data))
          errorData.push(validatePostTransformation(data))
        else errorData.push('')
      }
    }
    if (errorData?.length) setErrorMetadata(errorData)
  }, [postTransformation])

  useEffect(() => {
    if (form?.handleChange && fieldsMetadata?.length) {
      setIsInitialLoad(false)
      form.handleChange({
        target: {
          name: isDefault ? 'upload.postTransformation' : 'postTransformation',
          value: fieldsMetadata.map(data => {
            const ob = {
              type: convertLabelToValue(data.optionType),
            }
            if (data.protocol)
              ob['protocol'] = convertLabelToValue(data.protocol)
            if (data.value) ob['value'] = data.value
            return ob
          }),
        },
      })
    } else if (
      form?.handleChange &&
      !fieldsMetadata?.length &&
      !isInitialLoad
    ) {
      form.handleChange({
        target: {
          name: isDefault ? 'upload.postTransformation' : 'postTransformation',
          value: null,
        },
      })
    }
  }, [fieldsMetadata, form?.handleChange, setIsInitialLoad, isInitialLoad])

  return (
    <VStack width="full" alignItems="flex-start">
      {fieldsMetadata.map((metadata, index) => (
        <FormControl isInvalid={errorMetadata[index] !== ''}>
          <HStack width="full">
            <PostTransformationTypesField
              {...{
                fieldsMetadata,
                index,
                setFieldsMetadata,
                isDefault,
                isCommonSettings,
                type,
              }}
            />
            <CloseIcon
              cursor="pointer"
              boxSize="0.825rem"
              color={colors.appBattleshipGrey}
              onClick={() => {
                const newFieldsMetadata = [...fieldsMetadata]
                newFieldsMetadata.splice(index, 1)
                setFieldsMetadata(newFieldsMetadata)
              }}
            />
          </HStack>
          <FormErrorMessage>{errorMetadata[index]}</FormErrorMessage>
        </FormControl>
      ))}

      <Button
        color={colors.appClearBlue}
        fontWeight="medium"
        cursor="pointer"
        variant={'link'}
        onClick={() => {
          setFieldsMetadata([
            ...fieldsMetadata,
            {
              error: '',
              optionType: null,
              value: '',
            },
          ])
        }}
        isDisabled={fieldsMetadata?.length === MaxPostTransformation}
      >
        {t('plus_transformation', '+ Add new transformation')}
      </Button>
    </VStack>
  )
}

export default PostTransformationFields
